import { getDataFromStorage } from '@mmt/legacy-commons/AppState/LocalStorage';
import { getCtTrainsLegData, searchContextMapper } from '../PdtCtRailsListing';
import { generateUUID } from '@mmt/navigation/src/util';
import { getDateYYYYMMDD } from '@mmt/legacy-commons/Common/utils/DateUtils';
import { THANK_YOU_PDT_EVENTS, PDT_SCHEMA_ID, PDT_SCHEMA_NAME } from '../PdtCtRailsListing/PdtCtConstants';
import { COMMON_EVENT_TRACKING_CONTEXT, EXPERIMENT_DETAILS_CONTEXT } from '../CommonPdtConstants';
import { logRailsEventToPDT, getJourneyId, transformPayloadForXDM } from '../PdtV2Utils';
import { logGrafanaErrorMetrics } from 'apps/rails/src/Utils/RailsGrafanaTracker';
import { ENTITY_TYPE, PAGE, NEW_PDT_ERROR_CODES } from 'apps/rails/src/types/grafanaTracking.types';
import { ENTITY, PAGE_NAME_LANDING } from 'apps/rails/src/Utils/RailsConstant';
import { getLocusIdFromStationDetails } from 'apps/rails/src/pages/RailsLandingPage/Store/RailsLandingPageActions';
import { crypt } from 'apps/rails/src/Utils/cryptUtils';
import {
  LISTING_PDT_TRAIN_CARD_DETAILS,
  TRAVELLER_PDT_ADDON_DETAILS,
  CURRENCY_INR,
  ADDON_TYPE_FC,
  ADDON_TYPE_TG,
  PRODUCT_TYPE_TRAIN,
  LOB_RAILS,
  RESERVATION_TYPE_GENERAL,
  RESERVATION_TYPE_TATKAL,
  PAGE_LOAD_COMPONENT_TYPE,
  XDM_THANKYOU_LOAD_ID,
  AVAILABILITY_LAST_UPDATED_TEXT,
  DATA_SOURCE_IRCTC,
  PAGE_NAME_REVIEW,
  PAGE_NAME_THANKYOU,
  STORE_ID_MMT,
} from '../../../Utils/RailsConstant';
import { XDMWrapper, TARGETS } from '@gt-common/event-logger-web';
import { RailsLandingPageSchema } from '../../../schemas/railsSchemas';

const extractNumbers = (str: any) => {
  return str?.match(/\d+/g)?.join('') ?? '';
};

const parseDurationToMinutes = (duration: any): number | null => {
  if (duration === null || duration === undefined) return null;
  if (typeof duration === 'number') return duration;
  const num = Number(duration);
  if (!isNaN(num)) return num;
  const durationStr = String(duration);
  const hourMinMatch = durationStr.match(/(\d+)\s*(?:h|hr|hrs|hour|hours)\s*(?:(\d+)\s*(?:m|min|mins|minute|minutes)?)?/i);
  if (hourMinMatch) {
    return parseInt(hourMinMatch[1], 10) * 60 + (parseInt(hourMinMatch[2], 10) || 0);
  }
  const colonMatch = durationStr.match(/(\d+):(\d+)/);
  if (colonMatch) {
    return parseInt(colonMatch[1], 10) * 60 + parseInt(colonMatch[2], 10);
  }
  const minOnlyMatch = durationStr.match(/(\d+)\s*(?:m|min|mins|minute|minutes)/i);
  if (minOnlyMatch) {
    return parseInt(minOnlyMatch[1], 10);
  }
  return null;
};

const getDurationInMinutes = (duration: any): { duration: number } | {} => {
  const parsed = parseDurationToMinutes(duration);
  return parsed !== null ? { duration: parsed } : {};
};

export const logThankYouPageLoadEventSuccess = async (
  thankYouPageData: object,
  eventValue: object,
) => {
  try {
    const trainSelectedInListingPage = await getDataFromStorage(LISTING_PDT_TRAIN_CARD_DETAILS);
    const addOnAppliedInTravellerPage = await getDataFromStorage(TRAVELLER_PDT_ADDON_DETAILS);
    
    let locusResponse = null;
    let fromLocusData = {};
    let toLocusData = {};
    
    try {
      locusResponse = await getLocusIdFromStationDetails({
        from_station_code: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationCode,
        to_station_code: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationCode,
      });
      fromLocusData = {
        locusCode: locusResponse?.fromLocusCode,
        locusType: locusResponse?.sourceLocusType,
        locusV2Id: locusResponse?.sourceLocusV2Id,
        locusV2Type: locusResponse?.sourceLocusV2Type,
        cityLocusV2Id: locusResponse?.sourceCityLocusV2Id,
        lobCode: locusResponse?.fromLobCode,
        stationName: locusResponse?.fromStationName,
      };

      toLocusData = {
        locusCode: locusResponse?.toLocusCode,
        locusType: locusResponse?.destinationLocusType,
        locusV2Id: locusResponse?.destinationLocusV2Id,
        locusV2Type: locusResponse?.destinationLocusV2Type,
        cityLocusV2Id: locusResponse?.destinationCityLocusV2Id,
        lobCode: locusResponse?.toLobCode,
        stationName: locusResponse?.toStationName,
      };
    } catch (locusError) {
      console.error('Error calling locus API for PDT tracking:', locusError);
    }

    const travellerList = thankYouPageData?.travellersDetails?.travellerList || [];
    let adultCount = 0;
    let childCount = 0;
    let infantCount = 0;
    const childAges: number[] = [];

    travellerList.forEach((traveller: any) => {
      const age = parseInt(traveller.travellerAge, 10);
      if (age >= 0 && age <= 4) {
        infantCount++;
      } else if (age >= 5 && age <= 11) {
        childCount++;
        childAges.push(age);
      } else {
        adultCount++;
      }
    });

    const searchContext = {
      departureDate: thankYouPageData?.trainJourneyDetails?.boardingDateTime,
      originStation: {
        code: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationCode,
        stationName: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationName,
        ...fromLocusData,
      },
      destinationStation: {
        code: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationCode,
        stationName: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationName,
        ...toLocusData,
      },
      pax: {
        adult: adultCount,
        child: { count: childCount, ages: childAges },
        infant: infantCount,
      },
    };
    const priceBreakdownForCoupon = thankYouPageData?.priceBreakUp?.breakUpAmountAndLabelList || [];
    const couponItem = priceBreakdownForCoupon.find((item: any) => 
      item.label?.toLowerCase().includes('coupon discount')
    );
    const couponAmount = parseFloat(couponItem?.amount?.replace(/[₹,]/g, '') || '0');
    const appliedCoupon = couponAmount > 0 ? couponItem?.label : null;

    const bookingStatusValue = thankYouPageData?.bookingDetails?.bookingStatus;
    const bookingStatus = bookingStatusValue === 'F' ? 'failure' : (bookingStatusValue === 'S' ? 'success' : bookingStatusValue) || null;

    const bookingInfo = {
      booking_id: thankYouPageData?.bookingDetails?.bookingId,
      pnr_number: thankYouPageData?.bookingDetails?.pnrNo,
      currency: CURRENCY_INR,
      price: parseFloat(thankYouPageData?.priceBreakUp?.totalAmount?.replace(/[₹,]/g, '') || '0') || 0,
      tax: -1,
      from_date_time: Number(new Date(thankYouPageData?.trainJourneyDetails?.boardingDateTime)),
      to_date_time: Number(new Date(thankYouPageData?.trainJourneyDetails?.arrivalDateTime)),
      origin: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationName,
      destination: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationName,
      travel_class: thankYouPageData?.travellersDetails?.travellerList?.[0]?.journeyClass,
      booking_date: new Date()?.getDate(),
      is_self_booking: false,
      booking_confirmation_status: thankYouPageData?.bookingStatusDetails?.bookingStatusSubMessage,
      applied_coupon: appliedCoupon,
      status: bookingStatus,
    };

    const journeyData = await getJourneyId(searchContext);
    const journeyId = journeyData?.journey_id || 'crypt';

    const travellerInfo = thankYouPageData?.travellersDetails?.travellerList?.map((traveller: any, index: number) => {
      const firstName = traveller?.travellerName?.split(' ')?.[0] || '';
      const lastName = traveller?.travellerName?.split(' ')?.[1] || '';
      return {
        is_primary: index === 0,
        fname: firstName,
        lname: lastName,
        title: traveller?.travellerName || '',
        mobile_com_id: thankYouPageData?.bookingDetails?.mobileCommId || '',
        email_com_id: thankYouPageData?.bookingDetails?.emailCommId || '',
        mob_id: thankYouPageData?.bookingDetails?.userTicketMobile || '',
        email_id: thankYouPageData?.bookingDetails?.userTicketEmail || '',
        age: traveller?.travellerAge,
        gender: traveller?.travellerGender || '',
        type: 'traveller',
        ...(traveller?.travellerFoodPreference && { meal_preference: traveller.travellerFoodPreference }),
        ...(traveller?.travellerBirthPreference && { berth_preference: traveller.travellerBirthPreference }),
        reservation_type: (() => {
          if (traveller?.quota === 'TQ') return RESERVATION_TYPE_TATKAL;
          return RESERVATION_TYPE_GENERAL;
        })(),
      };
    }) || [];

    const travellerInfoHashed = thankYouPageData?.travellersDetails?.travellerList?.map((traveller: any, index: number) => {
      const firstName = traveller?.travellerName?.split(' ')?.[0] || '';
      const lastName = traveller?.travellerName?.split(' ')?.[1] || '';
      return {
        is_primary: index === 0,
        fname: firstName ? crypt(journeyId, firstName) : '',
        lname: lastName ? crypt(journeyId, lastName) : '',
        title: traveller?.travellerName ? crypt(journeyId, traveller.travellerName) : '',
        mobile_com_id: thankYouPageData?.bookingDetails?.mobileCommId || '',
        email_com_id: thankYouPageData?.bookingDetails?.emailCommId || '',
        mob_id: thankYouPageData?.bookingDetails?.userTicketMobile ? crypt(journeyId, thankYouPageData.bookingDetails.userTicketMobile) : '',
        email_id: thankYouPageData?.bookingDetails?.userTicketEmail ? crypt(journeyId, thankYouPageData.bookingDetails.userTicketEmail) : '',
        age: traveller?.travellerAge,
        gender: traveller?.travellerGender ? crypt(journeyId, traveller.travellerGender) : '',
        type: 'traveller',
        ...(traveller?.travellerFoodPreference && { meal_preference: traveller.travellerFoodPreference }),
        ...(traveller?.travellerBirthPreference && { berth_preference: traveller.travellerBirthPreference }),
        reservation_type: (() => {
          if (traveller?.quota === 'TQ') return RESERVATION_TYPE_TATKAL;
          return RESERVATION_TYPE_GENERAL;
        })(),
      };
    }) || [];

    const addonDetails: any[] = [];

    if (addOnAppliedInTravellerPage && Array.isArray(addOnAppliedInTravellerPage) && addOnAppliedInTravellerPage.length > 0) {
      addonDetails.push(...addOnAppliedInTravellerPage);
    } else {
      const totalAmount = parseFloat(thankYouPageData?.priceBreakUp?.totalAmount?.replace(/[₹,]/g, '') || '0');

      if (thankYouPageData?.confirmationGuaranteeDetails) {
        const priceBreakdown = thankYouPageData?.priceBreakUp?.breakUpAmountAndLabelList || [];
        const tgPremiumItem = priceBreakdown.find((item: any) => 
          item.label?.includes('3X Refund') || item.label?.includes('Trip Guarantee')
        );

        if (tgPremiumItem) {
          const numericAmount = parseFloat(tgPremiumItem.amount?.replace(/[₹,]/g, '') || '0');
          if (numericAmount > 0) {
            addonDetails.push({
              addontype: ADDON_TYPE_TG,
              id: ADDON_TYPE_TG,
              currency: CURRENCY_INR,
              includedunits: 1,
              unitprice: numericAmount,
              unittype: CURRENCY_INR,
              heading: 'Trip Guarantee',
              selected: true,
              available: true,
              variant: 0,
              premium_percentage: totalAmount > 0 ? Math.round((numericAmount / totalAmount) * 100) : 0,
            });
          }
        }
      }

      const priceBreakdown = thankYouPageData?.priceBreakUp?.breakUpAmountAndLabelList || [];
      const fcItem = priceBreakdown.find((item: any) => 
        item.label?.toLowerCase().includes('free cancellation') || 
        item.label?.toLowerCase().includes('zero cancellation')
      );

      if (fcItem) {
        const numericAmount = parseFloat(fcItem.amount?.replace(/[₹,]/g, '') || '0');
        if (numericAmount > 0) {
          addonDetails.push({
            addontype: ADDON_TYPE_FC,
            id: ADDON_TYPE_FC,
            currency: CURRENCY_INR,
            includedunits: 1,
            unitprice: numericAmount,
            unittype: CURRENCY_INR,
            heading: 'Free Cancellation',
            selected: true,
            available: true,
            variant: 0,
            premium_percentage: totalAmount > 0 ? Math.round((numericAmount / totalAmount) * 100) : 0,
          });
        }
      }
    }

    const trainSelected = trainSelectedInListingPage?.trainCardData;

    const trainDataToUse = trainSelected || {
      trainNumber: thankYouPageData?.trainJourneyDetails?.trainNumber,
      trainName: thankYouPageData?.trainJourneyDetails?.trainName,
      ...getDurationInMinutes(thankYouPageData?.trainJourneyDetails?.duration),
      departureTimeEpochInSec: new Date(thankYouPageData?.trainJourneyDetails?.boardingDateTime).getTime() / 1000,
      arrivalTimeEpochInSec: new Date(thankYouPageData?.trainJourneyDetails?.arrivalDateTime).getTime() / 1000,
      departureTime: thankYouPageData?.trainJourneyDetails?.boardingDateTime,
      arrivalTime: thankYouPageData?.trainJourneyDetails?.arrivalDateTime,
      frmStnCode: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationCode,
      frmStnCity: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationName,
      toStnCode: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationCode,
      toStnCity: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationName,
    };

    const trainData = {
      trainMetaData: trainDataToUse,
    };
    const index = 0;
    const legs = [];
    legs.push(getCtTrainsLegData(trainData, index + 1));

    const productList = [
      {
        id: XDM_THANKYOU_LOAD_ID,
        product_type: PRODUCT_TYPE_TRAIN,
        position: { v: trainSelectedInListingPage?.trainPosition, h: 0 },
        rank: 1,
        legs,
      },
    ];

    const eventDetail = {
      event_name: 'thankyou_load',
      event_type: 'page_visit',
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...THANK_YOU_PDT_EVENTS?.[eventValue],
      components: {
        id: XDM_THANKYOU_LOAD_ID,
        component_type: 'page_load',
        position: {
          h: 0,
          v: 0,
        },
        product_list: productList,
      },
      booking_info: bookingInfo,
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: PAGE_NAME_THANKYOU,
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: PDT_SCHEMA_ID,
        topic_name: PDT_SCHEMA_NAME,
        ...(await getJourneyId(searchContext)),
      },
      search_context: searchContextMapper(searchContext),
      event_detail: eventDetail,
      traveller_info: travellerInfo,
      ...(addonDetails.length > 0 && { addon_details: addonDetails }),
    };
    logRailsEventToPDT(pdtLoggerObj);

      try {
        const correctedLegs = [{
          id: XDM_THANKYOU_LOAD_ID,
          name: thankYouPageData?.trainJourneyDetails?.trainName || null,
          number: thankYouPageData?.trainJourneyDetails?.trainNumber || null,
          product_type: PRODUCT_TYPE_TRAIN,
          ...getDurationInMinutes(thankYouPageData?.trainJourneyDetails?.duration),
          fare_shown: parseFloat(thankYouPageData?.priceBreakUp?.totalAmount?.replace(/[₹,]/g, '') || '0') || null,
          is_dynamic_pricing_applicable: false,
          category: LOB_RAILS,
          availability_shown: thankYouPageData?.currAvailStatus || null,
          boarding_station: {
            code: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationCode || null,
            name: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationName || null,
            departure_time: thankYouPageData?.trainJourneyDetails?.boardingDateTime ? new Date(thankYouPageData.trainJourneyDetails.boardingDateTime).toLocaleTimeString('en-US', { hour12: false, timeZone: 'Asia/Kolkata' }).slice(0, 5) : null,
            departure_date: thankYouPageData?.trainJourneyDetails?.boardingDateTime ? new Date(thankYouPageData.trainJourneyDetails.boardingDateTime).toISOString() : null,
          },
          from_station: {
            code: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationCode || null,
            name: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationName || null,
            departure_time: thankYouPageData?.trainJourneyDetails?.boardingDateTime ? new Date(thankYouPageData.trainJourneyDetails.boardingDateTime).toLocaleTimeString('en-US', { hour12: false, timeZone: 'Asia/Kolkata' }).slice(0, 5) : null,
            departure_date: thankYouPageData?.trainJourneyDetails?.boardingDateTime ? new Date(thankYouPageData.trainJourneyDetails.boardingDateTime).toISOString() : null,
          },
          to_station: {
            code: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationCode || null,
            name: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationName || null,
            arrival_time: thankYouPageData?.trainJourneyDetails?.arrivalDateTime ? new Date(thankYouPageData.trainJourneyDetails.arrivalDateTime).toLocaleTimeString('en-US', { hour12: false, timeZone: 'Asia/Kolkata' }).slice(0, 5) : null,
            arrival_date: thankYouPageData?.trainJourneyDetails?.arrivalDateTime ? new Date(thankYouPageData.trainJourneyDetails.arrivalDateTime).toISOString() : null,
          },
          catalog_list: [{
            availability_last_updated: AVAILABILITY_LAST_UPDATED_TEXT,
            class_name: thankYouPageData?.travellersDetails?.travellerList?.[0]?.journeyClass || null,
            price: parseFloat(thankYouPageData?.priceBreakUp?.totalAmount?.replace(/[₹,]/g, '') || '0') || null,
            quota: thankYouPageData?.travellersDetails?.travellerList?.[0]?.quota || null,
            source_of_data: PAGE_NAME_LANDING,
            status: thankYouPageData?.currAvailStatus || null,
            trip_guarantee_available: (() => {
              if (thankYouPageData?.confirmationGuaranteeDetails) {
                return true;
              }
              const priceBreakdown = thankYouPageData?.priceBreakUp?.breakUpAmountAndLabelList || [];
              const tgItem = priceBreakdown.find((item: any) => {
                const label = item.label?.toLowerCase() || '';
                return label.includes('trip guarantee') || label.includes('3x refund');
              });
              return tgItem && parseFloat(tgItem.amount?.replace(/[₹,]/g, '') || '0') > 0;
            })(),
          }],
        }];

        const xdmPayload = {
          ...pdtLoggerObj,
          ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
          traveller_info: travellerInfoHashed,
          ...(addonDetails.length > 0 && { addon_details: addonDetails }),
          identities: {
            phone_number_hash: thankYouPageData?.bookingDetails?.mobileCommId || null,
            email_address_hash: thankYouPageData?.bookingDetails?.emailCommId || null,
          },
          page_context: {
            ...pdtLoggerObj.page_context,
            page_type: 'funnel',
            funnel_step: PAGE_NAME_THANKYOU,
            prev_page_name: PAGE_NAME_REVIEW,
            lob_category: LOB_RAILS,
            travel_store: {
              store_id: STORE_ID_MMT,
            },
          },
          event_detail: {
            ...pdtLoggerObj.event_detail,
            components: {
              ...pdtLoggerObj.event_detail.components,
              product_list: [{
                ...pdtLoggerObj.event_detail.components.product_list[0],
                legs: correctedLegs,
              }],
            },
          }
        };

        const xdmResponse = await XDMWrapper.logEvent({
          payload: transformPayloadForXDM(xdmPayload),
          schema: RailsLandingPageSchema,
          otherConfig: {
            lobName: LOB_RAILS,
            adobeKeyConfig: [],
          },
          target: TARGETS.ADOBE_DATA_LAYER,
          extraOptions: {
            lob: LOB_RAILS,
            destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
          },
        });
      } catch (xdmError) {
        console.error('Error sending thank you success to XDMWrapper:', xdmError);
      }

    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logging Thank You Page Load SUCCESS PDT event', error);
    logGrafanaErrorMetrics(
      PAGE.THANK_YOU,
      ENTITY_TYPE.PAGE,
      ENTITY.THANKYOU_PDT_ERROR_MEASUREMENT_PLATFORM_SUCCESS,
      error,
      NEW_PDT_ERROR_CODES.THANK_YOU_ERROR_CODE_SUCCESS,
    );
  }
};

export const logThankYouPageLoadEventFailure = async (thankYouPageData: any, eventValue: any) => {
  try {
    const trainSelectedInListingPage = await getDataFromStorage(LISTING_PDT_TRAIN_CARD_DETAILS);
    const addOnAppliedInTravellerPage = await getDataFromStorage(TRAVELLER_PDT_ADDON_DETAILS);
    
    let locusResponse = null;
    let fromLocusData = {};
    let toLocusData = {};
    
    try {
      locusResponse = await getLocusIdFromStationDetails({
        from_station_code: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationCode,
        to_station_code: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationCode,
      });
      fromLocusData = {
        locusCode: locusResponse?.fromLocusCode,
        locusType: locusResponse?.sourceLocusType,
        locusV2Id: locusResponse?.sourceLocusV2Id,
        locusV2Type: locusResponse?.sourceLocusV2Type,
        cityLocusV2Id: locusResponse?.sourceCityLocusV2Id,
        lobCode: locusResponse?.fromLobCode,
        stationName: locusResponse?.fromStationName,
      };

      toLocusData = {
        locusCode: locusResponse?.toLocusCode,
        locusType: locusResponse?.destinationLocusType,
        locusV2Id: locusResponse?.destinationLocusV2Id,
        locusV2Type: locusResponse?.destinationLocusV2Type,
        cityLocusV2Id: locusResponse?.destinationCityLocusV2Id,
        lobCode: locusResponse?.toLobCode,
        stationName: locusResponse?.toStationName,
      };
    } catch (locusError) {
      console.error('Error calling locus API for PDT tracking:', locusError);
    }

    const boardingDateTime = thankYouPageData?.trainJourneyDetails?.boardingDateTime;
    const boardingStation = thankYouPageData?.trainJourneyDetails?.boardingStation;
    const destinationStation = thankYouPageData?.trainJourneyDetails?.destinationStation;
    const travellerList = thankYouPageData?.travellersDetails?.travellerList || [];
    let adultCount = 0;
    let childCount = 0;
    let infantCount = 0;
    const childAges: number[] = [];

    travellerList.forEach((traveller: any) => {
      const age = parseInt(traveller.travellerAge, 10);
      if (age >= 0 && age <= 4) {
        infantCount++;
      } else if (age >= 5 && age <= 11) {
        childCount++;
        childAges.push(age);
      } else {
        adultCount++;
      }
    });

    const searchContext = {
      departureDate: boardingDateTime || null,
      originStation: {
        code: boardingStation?.stationCode || null,
        stationName: boardingStation?.stationName || null,
        ...fromLocusData,
      },
      destinationStation: {
        code: destinationStation?.stationCode || null,
        stationName: destinationStation?.stationName || null,
        ...toLocusData,
      },
      pax: {
        adult: adultCount,
        child: { count: childCount, ages: childAges },
        infant: infantCount,
      },
    };
    const priceBreakdownForCouponFailure = thankYouPageData?.priceBreakUp?.breakUpAmountAndLabelList || [];
    const couponItemFailure = priceBreakdownForCouponFailure.find((item: any) => 
      item.label?.toLowerCase().includes('coupon')
    );

    const journeyDataFailure = await getJourneyId(searchContext);
    const journeyIdFailure = journeyDataFailure?.journey_id || 'crypt';

    const travellerInfoFailure = thankYouPageData?.travellersDetails?.travellerList?.map((traveller: any, index: number) => {
      const firstName = traveller?.travellerName?.split(' ')?.[0] || '';
      const lastName = traveller?.travellerName?.split(' ')?.[1] || '';
      return {
        is_primary: index === 0,
        fname: firstName,
        lname: lastName,
        title: traveller?.travellerName || '',
        mobile_com_id: thankYouPageData?.bookingDetails?.mobileCommId || '',
        email_com_id: thankYouPageData?.bookingDetails?.emailCommId || '',
        mob_id: thankYouPageData?.bookingDetails?.userTicketMobile || '',
        email_id: thankYouPageData?.bookingDetails?.userTicketEmail || '',
        age: traveller?.travellerAge,
        gender: traveller?.travellerGender || '',
        type: 'traveller',
        ...(traveller?.travellerFoodPreference && { meal_preference: traveller.travellerFoodPreference }),
        ...(traveller?.travellerBirthPreference && { berth_preference: traveller.travellerBirthPreference }),
        reservation_type: (() => {
          if (traveller?.quota === 'TQ') return RESERVATION_TYPE_TATKAL;
          return RESERVATION_TYPE_GENERAL;
        })(),
      };
    }) || [];

    const travellerInfoHashedFailure = thankYouPageData?.travellersDetails?.travellerList?.map((traveller: any, index: number) => {
      const firstName = traveller?.travellerName?.split(' ')?.[0] || '';
      const lastName = traveller?.travellerName?.split(' ')?.[1] || '';
      return {
        is_primary: index === 0,
        fname: firstName ? crypt(journeyIdFailure, firstName) : '',
        lname: lastName ? crypt(journeyIdFailure, lastName) : '',
        title: traveller?.travellerName ? crypt(journeyIdFailure, traveller.travellerName) : '',
        mobile_com_id: thankYouPageData?.bookingDetails?.mobileCommId || '',
        email_com_id: thankYouPageData?.bookingDetails?.emailCommId || '',
        mob_id: thankYouPageData?.bookingDetails?.userTicketMobile ? crypt(journeyIdFailure, thankYouPageData.bookingDetails.userTicketMobile) : '',
        email_id: thankYouPageData?.bookingDetails?.userTicketEmail ? crypt(journeyIdFailure, thankYouPageData.bookingDetails.userTicketEmail) : '',
        age: traveller?.travellerAge,
        gender: traveller?.travellerGender ? crypt(journeyIdFailure, traveller.travellerGender) : '',
        type: 'traveller',
        ...(traveller?.travellerFoodPreference && { meal_preference: traveller.travellerFoodPreference }),
        ...(traveller?.travellerBirthPreference && { berth_preference: traveller.travellerBirthPreference }),
        reservation_type: (() => {
          if (traveller?.quota === 'TQ') return RESERVATION_TYPE_TATKAL;
          return RESERVATION_TYPE_GENERAL;
        })(),
      };
    }) || [];
    const couponAmountFailure = parseFloat(couponItemFailure?.amount?.replace(/[₹,]/g, '') || '0');
    const appliedCouponFailure = couponAmountFailure > 0 ? couponItemFailure?.label : null;

    const bookingStatusValue = thankYouPageData?.bookingDetails?.bookingStatus;
    const bookingStatus = bookingStatusValue === 'F' ? 'failure' : (bookingStatusValue === 'S' ? 'success' : bookingStatusValue) || null;

    const bookingInfo = {
      booking_id: thankYouPageData?.bookingDetails?.bookingId,
      currency: CURRENCY_INR,
      price: parseFloat(thankYouPageData?.priceBreakUp?.totalAmount?.replace(/[₹,]/g, '') || '0') || 0,
      tax: -1,
      from_date_time: null,
      destination: null,
      booking_date: new Date()?.getDate(),
      booking_confirmation_status: thankYouPageData?.bookingStatusDetails?.bookingStatusSubMessage,
      applied_coupon: appliedCouponFailure,
      status: bookingStatus,
    };

    const addonDetails: any[] = [];

    if (addOnAppliedInTravellerPage && Array.isArray(addOnAppliedInTravellerPage) && addOnAppliedInTravellerPage.length > 0) {
      addonDetails.push(...addOnAppliedInTravellerPage);
    } else {
      const totalAmount = parseFloat(thankYouPageData?.priceBreakUp?.totalAmount?.replace(/[₹,]/g, '') || '0');

      if (thankYouPageData?.confirmationGuaranteeDetails) {
        const priceBreakdown = thankYouPageData?.priceBreakUp?.breakUpAmountAndLabelList || [];
        const tgPremiumItem = priceBreakdown.find((item: any) => 
          item.label?.includes('3X Refund') || item.label?.includes('Trip Guarantee')
        );

        if (tgPremiumItem) {
          const numericAmount = parseFloat(tgPremiumItem.amount?.replace(/[₹,]/g, '') || '0');
          if (numericAmount > 0) {
            addonDetails.push({
              addontype: ADDON_TYPE_TG,
              id: ADDON_TYPE_TG,
              currency: CURRENCY_INR,
              includedunits: 1,
              unitprice: numericAmount,
              unittype: CURRENCY_INR,
              heading: 'Trip Guarantee',
              selected: true,
              available: true,
              variant: 0,
              premium_percentage: totalAmount > 0 ? Math.round((numericAmount / totalAmount) * 100) : 0,
            });
          }
        }
      }

      const priceBreakdown = thankYouPageData?.priceBreakUp?.breakUpAmountAndLabelList || [];
      const fcItem = priceBreakdown.find((item: any) => 
        item.label?.toLowerCase().includes('free cancellation') || 
        item.label?.toLowerCase().includes('zero cancellation')
      );

      if (fcItem) {
        const numericAmount = parseFloat(fcItem.amount?.replace(/[₹,]/g, '') || '0');
        if (numericAmount > 0) {
          addonDetails.push({
            addontype: ADDON_TYPE_FC,
            id: ADDON_TYPE_FC,
            currency: CURRENCY_INR,
            includedunits: 1,
            unitprice: numericAmount,
            unittype: CURRENCY_INR,
            heading: 'Free Cancellation',
            selected: true,
            available: true,
            is_preselected: true,
            variant: 0,
            premium_percentage: totalAmount > 0 ? Math.round((numericAmount / totalAmount) * 100) : 0,
          });
        }
      }
    }

    const trainSelected = trainSelectedInListingPage?.trainCardData;

    const trainDataToUse = trainSelected || {
      trainNumber: thankYouPageData?.trainJourneyDetails?.trainNumber,
      trainName: thankYouPageData?.trainJourneyDetails?.trainName,
      ...getDurationInMinutes(thankYouPageData?.trainJourneyDetails?.duration),
      departureTimeEpochInSec: new Date(thankYouPageData?.trainJourneyDetails?.boardingDateTime).getTime() / 1000,
      arrivalTimeEpochInSec: new Date(thankYouPageData?.trainJourneyDetails?.arrivalDateTime).getTime() / 1000,
      departureTime: thankYouPageData?.trainJourneyDetails?.boardingDateTime,
      arrivalTime: thankYouPageData?.trainJourneyDetails?.arrivalDateTime,
      frmStnCode: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationCode,
      frmStnCity: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationName,
      toStnCode: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationCode,
      toStnCity: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationName,
    };

    const trainData = {
      trainMetaData: trainDataToUse,
    };
    const index = 0;
    const legs = [];
    legs.push(getCtTrainsLegData(trainData, index + 1));

    const productList = [
      {
        id: XDM_THANKYOU_LOAD_ID,
        product_type: PRODUCT_TYPE_TRAIN,
        position: { v: trainSelectedInListingPage?.trainPosition, h: 0 },
        rank: 1,
        legs,
      },
    ];

    const eventDetail = {
      event_name: 'thankyou_load',
      event_type: 'page_visit',
      event_id: generateUUID(),
      event_timestamp: Date.now(),
      ...THANK_YOU_PDT_EVENTS?.[eventValue],
      components: {
        id: XDM_THANKYOU_LOAD_ID,
        component_type: 'page_load',
        position: {
          h: 0,
          v: 0,
        },
        product_list: productList,
      },
      booking_info: bookingInfo,
    };

    const pdtLoggerObj = {
      page_context: {
        lob: LOB_RAILS,
        page_name: PAGE_NAME_THANKYOU,
      },
      event_tracking_context: {
        ...(await COMMON_EVENT_TRACKING_CONTEXT()),
        template_id: PDT_SCHEMA_ID,
        topic_name: PDT_SCHEMA_NAME,
        ...(await getJourneyId(searchContext)),
      },
      search_context: searchContextMapper(searchContext),
      event_detail: eventDetail,
      traveller_info: travellerInfoFailure,
      ...(addonDetails.length > 0 && { addon_details: addonDetails }),
    };
    logRailsEventToPDT(pdtLoggerObj);
      try {
        const correctedLegs = [{
          id: XDM_THANKYOU_LOAD_ID,
          name: thankYouPageData?.trainJourneyDetails?.trainName || null,
          number: thankYouPageData?.trainJourneyDetails?.trainNumber || null,
          product_type: PRODUCT_TYPE_TRAIN,
          ...getDurationInMinutes(thankYouPageData?.trainJourneyDetails?.duration),
          fare_shown: parseFloat(thankYouPageData?.priceBreakUp?.totalAmount?.replace(/[₹,]/g, '') || '0') || null,
          is_dynamic_pricing_applicable: false,
          category: LOB_RAILS,
          availability_shown: thankYouPageData?.currAvailStatus || null,
          boarding_station: {
            code: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationCode || null,
            name: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationName || null,
            departure_time: thankYouPageData?.trainJourneyDetails?.boardingDateTime ? new Date(thankYouPageData.trainJourneyDetails.boardingDateTime).toLocaleTimeString('en-US', { hour12: false, timeZone: 'Asia/Kolkata' }).slice(0, 5) : null,
            departure_date: thankYouPageData?.trainJourneyDetails?.boardingDateTime ? new Date(thankYouPageData.trainJourneyDetails.boardingDateTime).toISOString() : null,
          },
          from_station: {
            code: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationCode || null,
            name: thankYouPageData?.trainJourneyDetails?.boardingStation?.stationName || null,
            departure_time: thankYouPageData?.trainJourneyDetails?.boardingDateTime ? new Date(thankYouPageData.trainJourneyDetails.boardingDateTime).toLocaleTimeString('en-US', { hour12: false, timeZone: 'Asia/Kolkata' }).slice(0, 5) : null,
            departure_date: thankYouPageData?.trainJourneyDetails?.boardingDateTime ? new Date(thankYouPageData.trainJourneyDetails.boardingDateTime).toISOString() : null,
          },
          to_station: {
            code: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationCode || null,
            name: thankYouPageData?.trainJourneyDetails?.destinationStation?.stationName || null,
            arrival_time: thankYouPageData?.trainJourneyDetails?.arrivalDateTime ? new Date(thankYouPageData.trainJourneyDetails.arrivalDateTime).toLocaleTimeString('en-US', { hour12: false, timeZone: 'Asia/Kolkata' }).slice(0, 5) : null,
            arrival_date: thankYouPageData?.trainJourneyDetails?.arrivalDateTime ? new Date(thankYouPageData.trainJourneyDetails.arrivalDateTime).toISOString() : null,
          },
          catalog_list: [{
            availability_last_updated: AVAILABILITY_LAST_UPDATED_TEXT,
            class_name: thankYouPageData?.travellersDetails?.travellerList?.[0]?.journeyClass || null,
            price: parseFloat(thankYouPageData?.priceBreakUp?.totalAmount?.replace(/[₹,]/g, '') || '0') || null,
            quota: thankYouPageData?.travellersDetails?.travellerList?.[0]?.quota || null,
            source_of_data: PAGE_NAME_LANDING,
            status: thankYouPageData?.currAvailStatus || null,
            trip_guarantee_available: (() => {
              if (thankYouPageData?.confirmationGuaranteeDetails) {
                return true;
              }
              const priceBreakdown = thankYouPageData?.priceBreakUp?.breakUpAmountAndLabelList || [];
              const tgItem = priceBreakdown.find((item: any) => {
                const label = item.label?.toLowerCase() || '';
                return label.includes('trip guarantee') || label.includes('3x refund');
              });
              return tgItem && parseFloat(tgItem.amount?.replace(/[₹,]/g, '') || '0') > 0;
            })(),
          }],
        }];

        const xdmPayload = {
          ...pdtLoggerObj,
          ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
          traveller_info: travellerInfoHashedFailure,
          ...(addonDetails.length > 0 && { addon_details: addonDetails }),
          identities: {
            phone_number_hash: thankYouPageData?.bookingDetails?.mobileCommId || null,
            email_address_hash: thankYouPageData?.bookingDetails?.emailCommId || null,
          },
          page_context: {
            ...pdtLoggerObj.page_context,
            page_type: 'funnel',
            funnel_step: PAGE_NAME_THANKYOU,
            prev_page_name: PAGE_NAME_REVIEW,
            lob_category: LOB_RAILS,
            travel_store: {
              store_id: STORE_ID_MMT,
            },
          },
          event_detail: {
            ...pdtLoggerObj.event_detail,
            components: {
              ...pdtLoggerObj.event_detail.components,
              product_list: [{
                ...pdtLoggerObj.event_detail.components.product_list[0],
                legs: correctedLegs,
              }],
            },
          }
        };

        const xdmResponse = await XDMWrapper.logEvent({
          payload: transformPayloadForXDM(xdmPayload),
          schema: RailsLandingPageSchema,
          otherConfig: {
            lobName: LOB_RAILS,
            adobeKeyConfig: [],
          },
          target: TARGETS.ADOBE_DATA_LAYER,
          extraOptions: {
            lob: LOB_RAILS,
            destinationUrl: typeof window !== 'undefined' ? window.location.href : '',
          },
        });
      } catch (xdmError: any) {
        console.error('ThankYou FAILURE - Error sending to XDMWrapper:', xdmError);
      }

    return pdtLoggerObj;
  } catch (error: any) {
    console.error('Error in logging Thank You Page Load FAILURE PDT event', error);
    logGrafanaErrorMetrics(
      PAGE.THANK_YOU,
      ENTITY_TYPE.PAGE,
      ENTITY.THANKYOU_PDT_ERROR_MEASUREMENT_PLATFORM_FAILURE,
      error,
      NEW_PDT_ERROR_CODES.THANK_YOU_ERROR_CODE_FAILURE,
    );
  }
};
